import React, { useRef, useEffect, useState } from 'react';
import { Episode } from '../types';
import { formatDuration } from '../utils/rssParser';

interface AudioPlayerProps {
  episode: Episode | null;
  autoPlay?: boolean;
  onEnded?: () => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ episode, autoPlay = true, onEnded }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    if (episode && audioRef.current) {
      audioRef.current.src = episode.audioUrl;
      audioRef.current.load();

      // Restore saved playback position
      const savedTime = localStorage.getItem(`playback-${episode.id}`);
      if (savedTime) {
        const time = parseFloat(savedTime);
        audioRef.current.currentTime = time;
        setCurrentTime(time);
      }

      // Only auto-play if explicitly requested
      if (autoPlay) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  }, [episode, autoPlay]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      if (episode) {
        localStorage.setItem(`playback-${episode.id}`, audio.currentTime.toString());
      }
    };

    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      if (onEnded) onEnded();
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [episode, onEnded]);

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    if (audioRef.current) {
      audioRef.current.volume = vol;
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15);
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 15);
    }
  };

  if (!episode) {
    return null;
  }

  return (
    <div className="audio-player">
      <audio ref={audioRef} />
      <div className="episode-info-bar">
        <span className="now-playing-label">Now playing:</span>
        <span className="now-playing-title">{episode.title}</span>
      </div>
      <div className="player-controls">
        <button onClick={skipBackward} className="skip-btn" title="Rewind 15 seconds">
          ⏪ 15s
        </button>
        <button onClick={togglePlayPause} className="play-btn">
          {isPlaying ? '⏸' : '▶'}
        </button>
        <button onClick={skipForward} className="skip-btn" title="Forward 15 seconds">
          15s ⏩
        </button>
        <span className="time">
          {formatDuration(currentTime)} / {formatDuration(duration)}
        </span>
        <input
          type="range"
          min="0"
          max={duration || 0}
          value={currentTime}
          onChange={handleSeek}
          className="seek-bar"
        />
        <button className="volume-btn">🔊</button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={volume}
          onChange={handleVolumeChange}
          className="volume-bar"
        />
        <button className="more-btn">⋮</button>
      </div>
    </div>
  );
};

export default AudioPlayer;
